import React, { useEffect, useMemo, useCallback, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { debounce } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import moment from 'moment';
import Clipboard from 'clipboard';
import styled, { keyframes } from 'styled-components';
import { color, space, typography } from 'styled-system';
import { selectOtherFilter } from 'redux-api/reselect/config';
import { useOutsideClick } from 'hooks';
import Input from 'theme/input';
import PopUp from 'theme/popup';
import Copy from 'theme/icons/copy';
import Write from 'theme/icons/write';
import Bin from 'theme/icons/bin';
import Loader from 'theme/icons/loader';
import translations from 'translations';

const Wrapper = styled.div`
  ${space}

  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  height: 58px;

  & > .btn-preset {
    display: flex;
    flex: 1;
    align-items: center;
  }
`;

const TextEllipsis = styled.p`
  ${typography}
  ${color}
  ${space}

  height: ${({ line }) => line * 22}px;
  overflow: hidden;
  text-overflow: ellipsis;
  /* stylelint-disable */
  display: -webkit-box;
  -webkit-box-orient: vertical;
  /* stylelint-enable */
  -webkit-line-clamp: ${({ line }) => line};
`;

const ActionsWrap = styled.div`
  display: flex;
  flex-direction: row;
`;

const ButtonStyled = styled.button`
  ${color}

  position: relative;
  display: ${({ isHidden }) => (isHidden ? 'none' : 'flex')};
  width: 38px;
  height: 38px;
  justify-content: center;
  align-items: center;
`;

const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
`;

const WrapLoader = styled.div`
  width: 18px;
  height: 18px;
  animation: ${rotate} 2s linear infinite;
`;

function PresetItem({ languageCode, error, isWaiting, payload, onSelect, onDelete, onEdit }) {
  const ref = useRef();
  const uuid = useRef(uuidv4()).current;

  const [visible, setVisible] = useState(false);
  const [out, setOut] = useState(true);
  const [touched, setTouched] = useState(false);
  const [titleInput, setTitleInput] = useState(payload.title);
  const [isEditable, setIsEditable] = useState(false);

  const otherFilter = useSelector(selectOtherFilter);
  const { analytics_type } = useSelector((state) => state.profile.payload);

  useEffect(() => {
    const clip = new Clipboard(`#a${uuid.replaceAll('-', '')}`);

    return () => clip.destroy();
  }, [payload.title, uuid]);

  const query = useMemo(() => {
    const { compare, dataType, dateFrom, dateTo, dateRange, filters } = payload;

    if (!compare || !dataType || !dateFrom || !dateTo || !dateRange) return null;

    let queryString = `dateRange=${dateRange}&compare=${compare}&dateTo=${dateTo}&dateFrom=${dateFrom}&dataType=${dataType}&version=7&enterprise=${
      analytics_type || 'standard'
    }`;

    if (filters) {
      queryString = `${queryString}&filters=${filters}`;
    }

    return `${window.location.href.split('?')[0]}?${queryString}`;
  }, [payload, analytics_type]);

  const formatDatesToCalendar = useMemo(
    () => [moment(payload.dateFrom), moment(payload.dateTo)],
    [payload],
  );

  const handleClick = useCallback(() => {
    let others = {};
    const { filters } = payload;

    if (filters) {
      const splitedFilters = filters.split(',');

      splitedFilters.forEach((filter) => {
        const splitedFilter = filter.split('=');

        if (splitedFilter.length !== 2) return;

        const { color: colorFilter } = otherFilter.find((el) => el.value === splitedFilter[0]);

        others = {
          ...others,
          [JSON.stringify(`${splitedFilter[0]}-${splitedFilter[1]}`)]: {
            type: splitedFilter[0],
            value: splitedFilter[1],
            color: colorFilter,
          },
        };
      });
    }

    let terms = {};
    const { terms: termsFilter } = payload;

    if (termsFilter) {
      const splitedFilters = termsFilter.split(',');

      splitedFilters.forEach((filter) => {
        const splitedFilter = filter.split('=');

        if (splitedFilter.length !== 2) return;

        const { color: colorFilter } = otherFilter.find((el) => el.value === splitedFilter[0]);

        terms = {
          ...terms,
          [JSON.stringify(`${splitedFilter[0]}-${splitedFilter[1]}`)]: {
            type: splitedFilter[0],
            value: splitedFilter[1],
            color: colorFilter,
          },
        };
      });
    }

    onSelect({ ...payload, dates: formatDatesToCalendar, others, terms });
  }, [onSelect, payload, formatDatesToCalendar, otherFilter]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const delayedHandleChange = useCallback(
    debounce((p) => {
      const trimmedValue = p.trim();
      if (payload.title !== trimmedValue) {
        setTouched(true);
        onEdit(payload.title, trimmedValue);
      }
    }, 500),
    [],
  );

  const handleChangeTitle = useCallback(
    (e) => {
      const { value } = e.target;

      setTitleInput(value);
      delayedHandleChange(value);
    },
    [delayedHandleChange],
  );

  const handleCopy = useCallback(() => {
    setOut(false);
    setVisible(true);

    setTimeout(() => {
      setOut(true);
    }, 3000);
  }, []);

  const handleDelete = useCallback(() => {
    onDelete(payload);
  }, [onDelete, payload]);

  const handleEdit = useCallback(() => {
    setIsEditable(!isEditable);
  }, [isEditable]);

  useOutsideClick(ref, () => {
    if (isWaiting) return;

    setTouched(false);
    setTitleInput(payload.title);
    setIsEditable(false);
  });

  return (
    <Wrapper my="xxs" px="xs" ref={ref}>
      {!isEditable ? (
        <button title={payload.title} type="button" className="btn-preset" onClick={handleClick}>
          <TextEllipsis
            fontSize="display"
            lineHeight="display"
            fontWeight="medium"
            color="grey_400"
            line="1"
          >
            {payload.title}
          </TextEllipsis>
        </button>
      ) : (
        <Input
          style={{
            width: '100%',
          }}
          meta={{
            touched,
            error,
          }}
          input={{
            onChange: handleChangeTitle,
            value: titleInput,
          }}
          isWaiting={isWaiting}
          addonAfter
          noError
        />
      )}
      <ActionsWrap>
        <ButtonStyled
          title={translations[languageCode].copy}
          id={`a${uuid.replaceAll('-', '')}`}
          isHidden={isEditable}
          type="button"
          data-clipboard-action="copy"
          data-clipboard-text={query}
          color="grey_400"
          onClick={handleCopy}
        >
          <PopUp visible={visible} out={out}>
            <span>{translations[languageCode].copied}</span>
          </PopUp>
          <Copy />
        </ButtonStyled>
        <ButtonStyled
          title={translations[languageCode].edit}
          color="grey_400"
          isHidden={isEditable}
          type="button"
          onClick={handleEdit}
        >
          <Write color="grey_400" />
        </ButtonStyled>
        <ButtonStyled
          title={translations[languageCode].delete}
          color="grey_400"
          type="button"
          onClick={handleDelete}
          disabled={isWaiting}
        >
          {isWaiting ? (
            <WrapLoader>
              <Loader width={18} />
            </WrapLoader>
          ) : (
            <Bin />
          )}
        </ButtonStyled>
      </ActionsWrap>
    </Wrapper>
  );
}

export default PresetItem;
